'use client';

import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useLearningSource,
  type LearningItem,
  type LearningRow,
} from '@akademi/site-builder-runtime';
import {
  resolveCourseCard,
  type CourseCardVariant,
  type ButtonRadius,
  type CertificateDisplay,
  type PopoverPosition,
  type ProgressTextPlace,
} from '../cards/registry';
import { getGridSpec } from '../cards/gridSpecs';
import { learningItemToCardProps } from '../cards/learningItemToCardProps';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { StaleIndicator } from '../_shared/StaleIndicator';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { prettify } from '../_shared/prettify';
import { tx, asList } from '../_shared/coerce';
import { sectionTitle } from '../_shared/styles';
import { CourseCardContainer } from './_CourseCardContainer';
import type { CourseCardStyle } from './CourseRow';

/**
 * Tabbed sibling of {@link CourseRow}. Same cards, same layout rules —
 * but instead of one fixed feed, the visitor flips between several
 * ({@code For you} / {@code Popular} / {@code Editor's picks} /
 * {@code New}) from a tab strip that sits on the heading row.
 *
 * <p>Each tab carries its own {@code source} slug (and optionally its
 * own {@code limit} + "see all" link); the active tab's row is fetched
 * from {@code LearningSource.fetchRow} on demand and memoised per
 * {@code source+limit} so flipping back to a visited tab is instant and
 * doesn't re-hit the API.
 */

type Localized = string | Record<string, string>;

export interface CourseRowTab {
  /** Tab strip label. Localized bundles are resolved via {@code tx}. */
  label?: Localized;
  /** Feed slug passed to {@code LearningSource.fetchRow}. */
  source?: string;
  /** Per-tab card count. Falls back to the block-level {@code limit}. */
  limit?: number;
  /** Per-tab "see all" link. Falls back to the block-level one. */
  seeAllHref?: string;
  seeAllLabel?: Localized;
}

export interface CourseRowTabsProps {
  /** Section heading, rendered inline-start of the tab strip. */
  title?: Localized;
  /** The tab strip. Each entry is one feed. */
  tabs?: CourseRowTab[];
  /** Which tab opens first (0-based). Clamped to the tab count. */
  defaultTabIndex?: number;
  /** Tab strip treatment: underlined labels (default, matches the
   *  reference design) or filled pills. */
  tabVariant?: 'underline' | 'pill';
  /** Where the tab strip sits in the heading row. Defaults to
   *  {@code 'start'} — right after the title. */
  tabAlign?: 'start' | 'center' | 'end';
  cardStyle?: CourseCardStyle;
  /** Show the course-card star rating. Defaults to shown. */
  showRating?: boolean;
  /** CTA button corner radius. Empty/default inherits the theme radius. */
  buttonRadius?: ButtonRadius;
  /** How the certificate marker renders on the udemy card's hover popover. */
  certificateDisplay?: CertificateDisplay;
  /** Autoplay the preview clip inside the udemy card's hover popover. */
  autoPlayPreview?: boolean;
  /** Where the udemy card's hover popover opens. */
  popoverPosition?: PopoverPosition;
  /** When true (default), the udemy card's hover-popover title links to
   *  the course, same as the card face. */
  popoverTitleLink?: boolean;
  /** Progress bar shown above the udemy card's footer for courses carrying
   *  user progress. {@code show} (default true) toggles the bar;
   *  {@code showText} (default false) adds the percentage label;
   *  {@code textPlace} positions that label beside or on top of the track. */
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
  /** Card count per tab, unless the tab overrides it. */
  limit?: number;
  seeAllLabel?: Localized;
  seeAllHref?: string;
  /** href template for individual course cards. When unset, inherits the
   *  card's own default ({@code /lxp/course/{id}}). */
  courseHrefTemplate?: string;
  /** Badge image URL for the certificate overlay on card thumbnails. */
  certificateBadgeUrl?: string;
  style?: CSSProperties;
  /** When true, keeps the section (and its tabs) mounted even if the
   *  first tab returns no items. */
  showWhenDataIsNull?: boolean;
  /** How a scrollable (rail) card style scrolls — {@code 'drag'} = free
   *  grab-to-scroll, {@code 'buttons'} = arrows-only stepper. */
  scrollMode?: 'drag' | 'buttons';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_SOURCE = 'popular_courses';
const DEFAULT_LIMIT = 12;
const MIN_LIMIT = 1;
const MAX_LIMIT = 12;

const FALLBACK_TABS: CourseRowTab[] = [{ source: DEFAULT_SOURCE }];

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function resolveLimit(raw: number | undefined, fallback: number): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : fallback;
  return clamp(n, MIN_LIMIT, MAX_LIMIT);
}

function resolveSource(raw: string | undefined): string {
  return (raw ?? '').trim() || DEFAULT_SOURCE;
}

export function CourseRowTabs({
  title,
  tabs: rawTabs,
  defaultTabIndex,
  tabVariant = 'underline',
  tabAlign = 'start',
  cardStyle = 'udemy',
  showRating = true,
  buttonRadius,
  certificateDisplay,
  autoPlayPreview,
  popoverPosition,
  popoverTitleLink,
  progressBar,
  seeAllLabel,
  seeAllHref,
  limit: rawLimit,
  courseHrefTemplate,
  certificateBadgeUrl,
  style,
  paddingOuter,
  paddingInner,
  showWhenDataIsNull = false,
  scrollMode = 'drag',
}: CourseRowTabsProps) {
  const { t } = useTranslation('site-builder-blocks');
  const learning = useLearningSource();
  const part = useBlockPart();

  const tabs = useMemo(() => {
    const list = asList<CourseRowTab>(rawTabs).filter((t) => t && typeof t === 'object');
    return list.length > 0 ? list : FALLBACK_TABS;
  }, [rawTabs]);

  const initialIndex = clamp(
    typeof defaultTabIndex === 'number' && Number.isFinite(defaultTabIndex) ? defaultTabIndex : 0,
    0,
    tabs.length - 1,
  );
  const [rawActive, setRawActive] = useState(initialIndex);
  // The authored tab list can shrink between renders (admin editing) —
  // clamp on read so we never index past the end.
  const active = clamp(rawActive, 0, tabs.length - 1);
  // Once the visitor has picked a tab, an empty feed must not unmount the
  // whole section: the tab strip is navigation and has to stay reachable.
  const [switched, setSwitched] = useState(false);

  const activeTab = tabs[active] ?? {};
  const source = resolveSource(activeTab.source);
  const blockLimit = resolveLimit(rawLimit, DEFAULT_LIMIT);
  const limit = resolveLimit(activeTab.limit, blockLimit);
  const cacheKey = `${source}::${limit}`;

  // Rows already fetched this mount, keyed by source+limit. Flipping back
  // to a visited tab renders from here instead of re-hitting the API.
  const cache = useRef(new Map<string, LearningRow>());

  const state = useAsyncResource<LearningRow>(
    async (signal) => {
      if (!learning) return null;
      const hit = cache.current.get(cacheKey);
      if (hit) return hit;
      const row = await learning.fetchRow({ source, limit, signal });
      if (row) cache.current.set(cacheKey, row);
      return row;
    },
    [learning, source, limit, cacheKey],
  );

  const rawId = useId();
  const scope = `crt${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectTab = useCallback((i: number) => {
    setRawActive(i);
    setSwitched(true);
  }, []);

  const onTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const last = tabs.length - 1;
      let next: number | null = null;
      if (e.key === 'ArrowRight') next = active === last ? 0 : active + 1;
      else if (e.key === 'ArrowLeft') next = active === 0 ? last : active - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      selectTab(next);
      tabRefs.current[next]?.focus();
    },
    [active, tabs.length, selectTab],
  );

  // The fetch state only reflects the tab in flight; a cached row keeps the
  // previous panel on screen so switching back doesn't flash a skeleton.
  const cachedRow = cache.current.get(cacheKey);
  const row: LearningRow | undefined = state.kind === 'loaded' ? state.data : cachedRow;

  const authorTitle = tx(title).trim();
  // Plain-text heading — also feeds the rail's aria-label, which must be a
  // string rather than the JSX node.
  const labelText = authorTitle || row?.label || prettify(source);

  const resolvedSeeAllHref =
    (typeof activeTab.seeAllHref === 'string' && activeTab.seeAllHref.trim()) ||
    (typeof seeAllHref === 'string' && seeAllHref.trim()) ||
    undefined;
  const resolvedSeeAllLabel =
    tx(activeTab.seeAllLabel).trim() || tx(seeAllLabel).trim() || t('shared.seeAll');

  const css = `
    .${scope}-head {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: clamp(12px, 2vw, 28px);
      margin-bottom: var(--space-6, 24px);
    }
    .${scope}-tabs {
      display: flex;
      align-items: center;
      gap: ${tabVariant === 'pill' ? 'var(--space-2, 8px)' : 'clamp(10px, 2vw, 28px)'};
      flex: 1 1 auto;
      justify-content: flex-${tabAlign};
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .${scope}-tabs::-webkit-scrollbar { display: none; width: 0; height: 0; }
    .${scope}-tab {
      appearance: none;
      background: none;
      border: none;
      padding: var(--space-2, 8px) var(--space-1, 4px);
      margin: 0;
      font: inherit;
      font-family: var(--font-family-body, inherit);
      font-size: var(--font-size-sm, 14px);
      font-weight: var(--font-weight-medium, 500);
      line-height: 1.4;
      white-space: nowrap;
      cursor: pointer;
      color: var(--color-text-secondary, #71717a);
      border-bottom: 2px solid transparent;
      transition: color 150ms ease, border-color 150ms ease, background 150ms ease;
    }
    .${scope}-tab:hover { color: var(--color-text-primary, #18181b); }
    .${scope}-tab:focus-visible {
      outline: 2px solid var(--color-brand-500, #6366f1);
      outline-offset: 2px;
      border-radius: var(--radius-sm, 4px);
    }
    .${scope}-tab[aria-selected="true"] {
      color: var(--color-text-primary, #18181b);
      font-weight: var(--font-weight-semibold, 600);
      border-bottom-color: var(--color-brand-500, #3b82f6);
    }
    .${scope}-tabs[data-variant="pill"] .${scope}-tab {
      padding: var(--space-1-5, 6px) var(--space-3-5, 14px);
      border: 1px solid var(--color-border-subtle, rgba(0, 0, 0, 0.12));
      border-radius: 999px;
    }
    .${scope}-tabs[data-variant="pill"] .${scope}-tab[aria-selected="true"] {
      background: var(--color-brand-500, #3b82f6);
      border-color: transparent;
      color: #fff;
    }
    .${scope}-seeall {
      flex: 0 0 auto;
      color: var(--color-brand-600, #d6007f);
      font-size: var(--font-size-sm, 14px);
      font-weight: var(--font-weight-medium, 500);
      text-decoration: none;
      white-space: nowrap;
    }
    .${scope}-seeall:hover { text-decoration: underline; }
  `;

  const header: ReactNode = (
    <div className={`${scope}-head`}>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      {labelText && (
        <h2 {...part('title')} style={sectionTitle}>
          {labelText}
          {row?.stale && (
            <span style={{ marginLeft: 12, display: 'inline-block', verticalAlign: 'middle' }}>
              <StaleIndicator />
            </span>
          )}
        </h2>
      )}
      <div
        role="tablist"
        aria-label={labelText || 'Courses'}
        className={`${scope}-tabs`}
        data-variant={tabVariant}
        onKeyDown={onTabKeyDown}
        {...part('tablist')}
      >
        {tabs.map((tab, i) => {
          const isActive = i === active;
          return (
            <button
              key={i}
              type="button"
              role="tab"
              id={`${scope}-tab-${i}`}
              aria-selected={isActive}
              aria-controls={`${scope}-panel`}
              tabIndex={isActive ? 0 : -1}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              className={`${scope}-tab`}
              onClick={() => selectTab(i)}
              {...part('tab', i)}
            >
              {tx(tab.label) || prettify(resolveSource(tab.source))}
            </button>
          );
        })}
      </div>
      {resolvedSeeAllHref && (
        <a {...part('action')} href={resolvedSeeAllHref} className={`${scope}-seeall`}>
          {resolvedSeeAllLabel} →
        </a>
      )}
    </div>
  );

  const gridSpec = getGridSpec(cardStyle);
  const skeletonLayout = gridSpec.kind === 'rail' ? 'rail' : gridSpec.kind;

  const shell = (body: ReactNode) => (
    <SectionShell
      style={style}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      maxWidth="theme"
    >
      {header}
      <div
        id={`${scope}-panel`}
        role="tabpanel"
        aria-labelledby={`${scope}-tab-${active}`}
        tabIndex={-1}
        {...part('panel')}
      >
        {body}
      </div>
    </SectionShell>
  );

  if (!learning) {
    return shell(<AdapterPlaceholder blockType="CourseRowTabs" adapterName="LearningSource" />);
  }

  if (state.kind === 'loading' && !cachedRow) {
    return shell(<SkeletonRow count={limit} layout={skeletonLayout} />);
  }

  const courses: LearningItem[] = row?.items ?? [];

  if (courses.length === 0) {
    // Same contract as CourseRow: a silent block unless the author opted
    // into showing the empty state — but never yank the tab strip out from
    // under a visitor who has already used it.
    if (!showWhenDataIsNull && !switched) return null;
    return shell(
      <EmptyState
        title={t('courseRow.empty.title')}
        hint={t('courseRowTabs.empty.hint')}
        diagnostic={source}
      />,
    );
  }

  const Card = resolveCourseCard(cardStyle as CourseCardVariant);

  return shell(
    // Keyed by feed so a rail restarts at its first card on tab change
    // instead of keeping the previous tab's scroll offset.
    <CourseCardContainer key={cacheKey} spec={gridSpec} label={labelText} scrollMode={scrollMode}>
      {courses.map((c) => (
        <Card
          key={c.id}
          showRating={showRating}
          buttonRadius={buttonRadius}
          certificateDisplay={certificateDisplay}
          autoPlayPreview={autoPlayPreview}
          popoverPosition={popoverPosition}
          popoverTitleLink={popoverTitleLink}
          progressBar={progressBar}
          course={learningItemToCardProps(c, {
            ...(courseHrefTemplate ? { hrefTemplate: courseHrefTemplate } : {}),
            certificateBadgeUrl,
          })}
        />
      ))}
    </CourseCardContainer>,
  );
}
